(function () {
  'use strict';

  // Flow announces the cart changes its own drawer handles on the document
  // element. It does not announce the rest, so the write endpoints get watched
  // too — see watchCart below.
  var CART_UPDATE_EVENT = 'wetheme-cart-update';
  var CART_WRITE_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var SCROLL_THRESHOLD = 8;
  var SEARCH_DEBOUNCE = 200;
  var MIN_QUERY_LENGTH = 2;

  var cartBadges = [];
  var cartWatched = false;

  function renderCartCount(count) {
    cartBadges.forEach(function (badge) {
      badge.count.textContent = count;
      badge.link.hidden = count === 0;
    });
  }

  function refreshCartCount() {
    window
      .fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (cart) renderCartCount(cart.item_count);
      })
      .catch(function () {
        // A failed count is not worth surfacing; the next page load corrects it.
      });
  }

  // Flow's event is the fast path, but it only fires where its own drawer runs:
  // an add that leaves the drawer closed never reaches it, so the badge sat out
  // of date until the next page load. Watching the cart endpoints themselves
  // catches every write, whoever made it, and re-reads the real count. Both
  // paths land on renderCartCount, so a doubled-up update is just a repaint.
  function watchCart() {
    if (cartWatched) return;
    cartWatched = true;

    document.documentElement.addEventListener(CART_UPDATE_EVENT, function (event) {
      var count = event.detail && event.detail.cart && event.detail.cart.item_count;
      if (typeof count === 'number') renderCartCount(count);
    });

    // Coming back through history can restore a page whose badge predates the
    // cart it is looking at.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) refreshCartCount();
    });

    if (typeof window.fetch === 'function' && !window.fetch.stnWatched) {
      var nativeFetch = window.fetch;
      var watchedFetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        // Always window, never the caller's `this`: a bare fetch() call in
        // strict-mode code would hand native fetch an undefined receiver.
        var request = nativeFetch.apply(window, arguments);
        if (CART_WRITE_URL.test(url)) {
          // The caller gets its own promise back untouched; this branch only
          // observes, and swallows its own rejection so none is left unhandled.
          request.then(function (response) {
            if (response.ok) refreshCartCount();
          }, function () {});
        }
        return request;
      };
      watchedFetch.stnWatched = true;
      window.fetch = watchedFetch;
    }

    // Apps and older scripts still add to cart over XMLHttpRequest.
    var xhr = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (xhr && typeof xhr.open === 'function' && !xhr.send.stnWatched) {
      var nativeOpen = xhr.open;
      var nativeSend = xhr.send;

      xhr.open = function (method, url) {
        this.stnCartWrite = CART_WRITE_URL.test(String(url || ''));
        return nativeOpen.apply(this, arguments);
      };

      xhr.send = function () {
        if (this.stnCartWrite) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) refreshCartCount();
          });
        }
        return nativeSend.apply(this, arguments);
      };
      xhr.send.stnWatched = true;
    }
  }

  function init(root) {
    if (!root || root.dataset.stnReady === 'true') return;
    root.dataset.stnReady = 'true';

    var panel = root.querySelector('.stn__panel');
    var toggle = root.querySelector('[data-stn-toggle]');
    var searchButton = root.querySelector('[data-stn-search]');
    var searchInput = root.querySelector('.stn__search-input');
    var cartLink = root.querySelector('[data-stn-cart]');
    var cartCount = root.querySelector('[data-stn-cart-count]');
    var lastFocused = null;
    var cartBadge = null;

    // First, ahead of the menu wiring below: the badge is the one part of the
    // bar that has to keep working even if something later in here throws.
    if (cartLink && cartCount) {
      cartBadge = { link: cartLink, count: cartCount };
      cartBadges.push(cartBadge);
      watchCart();
    }

    function isOpen() {
      return root.classList.contains('stn--open');
    }

    function open() {
      lastFocused = document.activeElement;
      panel.hidden = false;
      root.classList.add('stn--open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('stn-locked');
      if (searchInput) searchInput.tabIndex = 0;
    }

    function close() {
      panel.hidden = true;
      root.classList.remove('stn--open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('stn-locked');
      if (searchInput) {
        searchInput.tabIndex = -1;
        searchInput.value = '';
      }
      clearResults();
      if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
      lastFocused = null;
    }

    function onKeydown(event) {
      if (event.key === 'Escape' && isOpen()) close();
    }

    // A click outside the nav closes it; clicks inside the panel are the menu itself.
    function onDocumentClick(event) {
      if (isOpen() && !root.contains(event.target)) close();
    }

    function onScroll() {
      root.classList.toggle('stn--scrolled', window.scrollY > SCROLL_THRESHOLD);
    }

    toggle.addEventListener('click', function () {
      if (isOpen()) close();
      else open();
    });

    // While closed the field is visibility:hidden, or display:none on mobile,
    // and an unrendered element cannot take focus. Opening only adds a class,
    // so wait for the browser to apply it before focusing: one frame to let
    // the style land, a second to be sure layout has followed.
    function focusSearch() {
      if (!searchInput) return;

      if (isOpen()) {
        searchInput.focus();
        return;
      }

      open();
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          searchInput.focus();
        });
      });
    }

    if (searchButton) {
      searchButton.addEventListener('click', focusSearch);
    }

    // Predictive search ------------------------------------------------------
    var results = root.querySelector('[data-stn-results]');
    var grid = root.querySelector('.stn__grid');
    var predictiveUrl = root.dataset.predictiveUrl;
    var searchLimit = root.dataset.searchLimit || 6;
    var searchTimer = null;
    var searchController = null;

    function showResults(on) {
      root.classList.toggle('stn--searching', on);
      if (searchInput) searchInput.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (grid) grid.hidden = on;
    }

    function clearResults() {
      if (searchController) searchController.abort();
      searchController = null;
      if (results) results.innerHTML = '';
      showResults(false);
    }

    function renderResults(html) {
      // The endpoint returns the whole section wrapper; take only our contents.
      var parsed = document.createElement('div');
      parsed.innerHTML = html;
      var inner = parsed.querySelector('[data-stn-results-inner]');
      results.innerHTML = inner ? inner.innerHTML : '';
      showResults(true);
    }

    function runSearch(query) {
      if (!results || !predictiveUrl) return;

      // Each keystroke supersedes the last, so drop any request still in
      // flight. Otherwise a slow early response can land after a later one and
      // leave stale results on screen.
      if (searchController) searchController.abort();
      searchController = new AbortController();

      var url =
        predictiveUrl +
        '?q=' + encodeURIComponent(query) +
        '&section_id=subtext-search-results' +
        '&resources[type]=product,collection,page' +
        '&resources[limit]=' + encodeURIComponent(searchLimit);

      fetch(url, { signal: searchController.signal })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(renderResults)
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          // A failed lookup should leave the menu usable rather than blank.
          clearResults();
        });
    }

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        var query = searchInput.value.trim();
        clearTimeout(searchTimer);

        if (query.length < MIN_QUERY_LENGTH) {
          clearResults();
          return;
        }

        searchTimer = setTimeout(function () {
          runSearch(query);
        }, SEARCH_DEBOUNCE);
      });

      // Arrow keys walk the results; Escape steps back to the field, then out.
      searchInput.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown') return;
        var first = results && results.querySelector('[data-stn-result]');
        if (!first) return;
        event.preventDefault();
        first.focus();
      });
    }

    if (results) {
      results.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        var items = Array.prototype.slice.call(results.querySelectorAll('[data-stn-result]'));
        var index = items.indexOf(document.activeElement);
        if (index === -1) return;

        event.preventDefault();
        var next = event.key === 'ArrowDown' ? index + 1 : index - 1;

        if (next < 0) {
          searchInput.focus();
        } else if (next < items.length) {
          items[next].focus();
        }
      });
    }

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onDocumentClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    // The theme editor tears sections down and rebuilds them; drop our
    // document-level listeners and the body styles we set with it.
    root.stnDestroy = function () {
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onDocumentClick);
      window.removeEventListener('scroll', onScroll);
      if (cartBadge) cartBadges.splice(cartBadges.indexOf(cartBadge), 1);
      document.body.classList.remove('stn-locked');
    };

    onScroll();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('.stn').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.stn').forEach(function (root) {
      if (root.stnDestroy) root.stnDestroy();
    });
  });
})();
